/* ============================================================
   TPW Mega Search v2 — command-bar overlay controller.
   Keeps Shopify's predictive engine: fetches the predictive API and renders
   sections/tpw-mega-search.liquid (?section_id=tpw-mega-search), then dresses the
   result with the adaptive-refine row and the zero-state.
   Bespoke shell/markup only — no engine rewrite. Vanilla JS, no deps.

   COMPLIANCE (2026-07-29): the TPS/flavours deep-link routing (TPS_URL, the
   53-term flavour/tobacco/brand dictionary, matchFlavour(), and the Hand-Off
   bridge markup) was REMOVED — G6 of the TPW->TPS cross-promo remediation.
   See snippets/header-sister-link.liquid for the full compliance record. Hardware
   search (predictive fetch, refine pills, keyboard nav, zero-state) is untouched.
   ============================================================ */
(function () {
  "use strict";

  // ---- canonical facts (mirror [[tpw-conversion-facts]]) ----
  var WHATSAPP_URL = "https://wa.me/message/BZUY3PWGSFAEJ1";

  // Adaptive refine: query keyword -> contextual filters that matter for that part.
  // Pills filter the shown rows by loose substring match against each product's
  // type+tags+title, so they work against the real catalogue without pre-known tags.
  var REFINE = {
    bowl: ["Phunnel", "Glass", "Silicone", "Clay"],
    charcoal: ["26mm", "28mm", "Cube", "Flat"],
    coal: ["26mm", "28mm", "Cube", "Flat"],
    hose: ["Washable", "Silicone", "Traditional"],
    hmd: ["With foil", "Foil-free", "Electric"],
    heat: ["With foil", "Foil-free", "Electric"],
    pipe: ["Traditional", "Modern", "Travel"],
    hookah: ["Traditional", "Modern", "Travel"],
    base: ["Russian", "Egyptian", "Mini"]
  };

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  customElements.define(
    "tpw-mega-search",
    class extends HTMLElement {
      connectedCallback() {
        this.scrim = this.querySelector(".ms-scrim");
        this.panel = this.querySelector(".ms-panel");
        this.input = this.querySelector(".ms-q");
        this.refine = this.querySelector(".ms-refine");
        this.body = this.querySelector(".ms-body");
        this.empty = this.querySelector(".ms-empty");
        this.live = this.querySelector(".ms-live");
        this.countEl = this.querySelector(".ms-count");
        this.seeall = this.querySelector(".ms-seeall");
        this.links = this.querySelector(".ms-links");
        this.predictiveUrl = this.getAttribute("data-predictive-url") || "/search/suggest";
        this.searchUrl = this.getAttribute("data-search-url") || "/search";

        this.sel = -1;
        this.items = [];
        this.activeRefine = null;
        this.refinePills = null;
        this.lastQuery = "";
        this.debounceTimer = null;
        this.abort = null;
        this.lastTrigger = null;
        this._uid = 0;

        // FUNC-07: if the section markup ever drifts (a renamed/removed node), do NOT bind.
        // The capture-phase header hook (bind) preventDefaults the native search box; installing
        // it when our overlay is broken would leave the whole site with NO working search. Bail
        // out and let the native predictive search keep working.
        if (!this.input || !this.panel || !this.body || !this.live) return;

        this.bind();
        this.collectItems();
      }

      bind() {
        var self = this;

        // close on scrim / esc button
        this.addEventListener("click", function (e) {
          if (e.target.closest("[data-ms-close]")) {
            e.preventDefault();
            self.close();
          }
        });

        // typing
        this.input.addEventListener("input", function () {
          self.sel = -1;
          self.input.removeAttribute("aria-activedescendant");
          self.activeRefine = null;
          var q = self.input.value.trim();
          if (!q) {
            self.showEmpty();
            return;
          }
          clearTimeout(self.debounceTimer);
          self.debounceTimer = setTimeout(function () {
            self.run(q);
          }, 170);
        });

        // keyboard inside the overlay
        this.input.addEventListener("keydown", function (e) {
          if (e.key === "ArrowDown") { e.preventDefault(); self.move(1); }
          else if (e.key === "ArrowUp") { e.preventDefault(); self.move(-1); }
          else if (e.key === "Enter") {
            var cur = self.items[self.sel];
            if (cur && cur.getAttribute("href")) { window.location.href = cur.getAttribute("href"); }
            else if (self.input.value.trim()) { window.location.href = self.searchUrl + "?q=" + encodeURIComponent(self.input.value.trim()); }
          }
        });

        // global shortcuts
        document.addEventListener("keydown", function (e) {
          var tag = (document.activeElement && document.activeElement.tagName) || "";
          var typing = /input|textarea|select/i.test(tag) || (document.activeElement && document.activeElement.isContentEditable);
          if (e.key === "/" && !typing && !self.classList.contains("open")) {
            e.preventDefault(); self.open();
          } else if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
            e.preventDefault(); self.classList.contains("open") ? self.close() : self.open();
          } else if (e.key === "Escape" && self.classList.contains("open")) {
            self.close();
          }
        });

        // intercept the existing header search box(es) -> open the overlay instead of
        // the native predictive dropdown. Capture phase so we win before the native one.
        // We preventDefault (stops native focus + the magnifier <button> form-submit) but
        // we do NOT stopPropagation — the theme's own "click-outside-to-close" menu/drawer
        // listeners must still receive the event so an open menu/cart can close.
        var headerHook = function (e) {
          var field = e.target.closest && e.target.closest(".header-search .search__input, .header-search .search__button, [data-tpw-search-open]");
          if (!field || self.contains(field)) return;
          e.preventDefault();
          self.lastTrigger = field;
          self.open();
        };
        document.addEventListener("mousedown", headerHook, { capture: true, passive: false });
        document.addEventListener("touchstart", headerHook, { capture: true, passive: false });
        document.addEventListener("click", headerHook, { capture: true, passive: false });
        // keyboard Tab into the header field: open the overlay. open() pulls focus to our
        // input, which implicitly blurs the header input (no explicit blur churn needed).
        document.addEventListener("focusin", function (e) {
          if (self.classList.contains("open")) return;
          // backstop: ignore focus that lands right after a close (prevents reopen loop)
          if (self._closedAt && Date.now() - self._closedAt < 500) return;
          var inp = e.target.closest && e.target.closest(".header-search .search__input");
          if (!inp || self.contains(inp)) return;
          self.lastTrigger = inp;
          self.open();
        }, true);

        // focus trap: keep Tab / Shift+Tab inside the dialog panel (aria-modal).
        this.panel.addEventListener("keydown", function (e) {
          if (e.key !== "Tab") return;
          var f = self.panel.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])');
          f = Array.prototype.filter.call(f, function (el) { return !el.hidden && el.offsetParent !== null; });
          if (!f.length) return;
          var first = f[0], last = f[f.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        });

        // re-collect selectable items whenever the live area changes
        this._mo = new MutationObserver(function () { self.collectItems(); });
        this._mo.observe(this.live, { childList: true, subtree: true });
      }

      disconnectedCallback() {
        // never leak the scroll lock or the observer if torn down while open
        document.documentElement.classList.remove("tpw-ms-lock");
        document.body.classList.remove("tpw-ms-lock");
        if (this._mo) this._mo.disconnect();
      }

      open() {
        if (this.classList.contains("open")) return;
        this.classList.add("open");
        document.documentElement.classList.add("tpw-ms-lock");
        document.body.classList.add("tpw-ms-lock");
        this.input.setAttribute("aria-expanded", "true");
        var self = this;
        // B-11: the overlay is display:none until .open is added THIS tick, so the
        // input is not yet focusable this frame — a single requestAnimationFrame
        // fires BEFORE the first paint of the just-displayed panel, so .focus() was
        // racing the layout and the first keystroke(s) were dropped. Focus with a
        // DOUBLE rAF (first rAF: the display:block + open styles are committed;
        // second rAF: after the first paint, the input is genuinely focusable) so
        // the caret is in the field before the user can type. preventScroll stops
        // the focus from fighting the scroll lock. This still runs after paint, so
        // iOS still raises the keyboard.
        var focusInput = function () { try { self.input.focus({ preventScroll: true }); } catch (e) { self.input.focus(); } };
        requestAnimationFrame(function () { requestAnimationFrame(focusInput); });
        // Belt-and-suspenders: if the input is already focusable (overlay was only
        // visually hidden, or the browser committed layout synchronously), focus
        // immediately too — focus() is idempotent, so a later rAF re-focus is a
        // harmless no-op, but this catches the earliest possible keystroke.
        focusInput();
        if (this.input.value.trim()) { this.run(this.input.value.trim()); }
        else { this.showEmpty(); }
      }

      close() {
        this.classList.remove("open");
        document.documentElement.classList.remove("tpw-ms-lock");
        document.body.classList.remove("tpw-ms-lock");
        this.input.setAttribute("aria-expanded", "false");
        this.input.value = "";
        this.activeRefine = null;
        this.sel = -1;
        this.input.removeAttribute("aria-activedescendant");
        this.showEmpty();
        this._closedAt = Date.now();
        // A11Y-03: restore focus to the control that opened the overlay (keyboard users keep
        // their place instead of being dropped at the top of the page). Safe now: the focusin
        // handler ignores focus that lands within 500ms of _closedAt, so refocusing the trigger
        // cannot reopen the overlay. Fall back to a clean blur if we have no remembered trigger.
        if (this.lastTrigger && this.lastTrigger.focus && document.contains(this.lastTrigger)) {
          this.lastTrigger.focus();
        } else {
          if (this.input && this.input.blur) this.input.blur();
          if (document.activeElement && document.activeElement.blur && this.contains(document.activeElement)) {
            document.activeElement.blur();
          }
        }
      }

      showEmpty() {
        this.setWorking(false);
        if (this.abort) { this.abort.abort(); this.abort = null; }
        this.refine.classList.remove("show");
        this.refine.innerHTML = "";
        this.countEl.textContent = "";
        this.live.hidden = true;
        this.live.innerHTML = "";
        this.empty.hidden = false;
        this.seeall.hidden = true;
        this.links.hidden = false;
        this.collectItems();
      }

      // P3 "Sweep": the shared Beam (assets/tpw-loading.css) under the search field
      // while the predictive fetch runs + the current results dim. Reuses the
      // foundation's .tpwl-is-working activation; no spinner, no new motion language.
      setWorking(on) {
        if (this.panel) this.panel.classList.toggle("tpwl-is-working", !!on);
        if (this.body) {
          if (on) this.body.setAttribute("aria-busy", "true");
          else this.body.removeAttribute("aria-busy");
        }
      }

      run(q) {
        var self = this;
        var ql = q.toLowerCase();
        this.lastQuery = ql;
        if (this.abort) this.abort.abort();
        this.abort = new AbortController();
        this.setWorking(true);

        var url =
          this.predictiveUrl +
          "?q=" + encodeURIComponent(q) +
          "&resources[type]=product,collection,page,article" +
          "&resources[limit]=8&resources[limit_scope]=each" +
          "&section_id=tpw-mega-search";

        fetch(url, { signal: this.abort.signal })
          .then(function (r) { return r.text(); })
          .then(function (html) {
            if (self.lastQuery !== ql) return; // a newer query already fired
            var doc = new DOMParser().parseFromString(html, "text/html");
            var node = doc.querySelector("#shopify-section-tpw-mega-search") || doc.querySelector(".ms-results");
            var results = node ? node.querySelector(".ms-results") : null;
            self.render(ql, q, results);
          })
          .catch(function (err) {
            if (err && err.name === "AbortError") return;
            // fail soft: send them to the full search page
            self.renderError(q);
          });
      }

      render(ql, qRaw, results) {
        this.setWorking(false);
        var count = results ? parseInt(results.getAttribute("data-count") || "0", 10) : 0;
        var searchurl = (results && results.getAttribute("data-searchurl")) || (this.searchUrl + "?q=" + encodeURIComponent(qRaw));
        this.currentSearchUrl = searchurl;
        var hasJump = results && results.querySelector(".ms-jump:not(.ms-jump--help)");

        this.empty.hidden = true;
        this.live.hidden = false;
        this.links.hidden = false;

        // ZERO STATE: nothing matched and no brand/category jump
        if ((!count || count === 0) && !hasJump) {
          this.refine.classList.remove("show");
          this.refine.innerHTML = "";
          this.countEl.textContent = "";
          this.seeall.hidden = true;
          this.live.innerHTML = this.zeroHTML(qRaw);
          this.collectItems();
          return;
        }

        // adaptive refine row
        this.buildRefine(ql);

        // assemble live content: section results
        var html = "";
        html += results ? results.innerHTML : "";
        this.live.innerHTML = html;

        // count + see-all (suggest caps results, so this is a preview count, not a grand total)
        var shownCount = this.live.querySelectorAll(".ms-row").length;
        this.countEl.textContent = shownCount ? shownCount + (shownCount === 1 ? " result" : " results") : "";
        if (shownCount) {
          this.seeall.hidden = false;
          this.seeall.setAttribute("href", searchurl);
          this.seeall.textContent = 'See all results for "' + qRaw + '" →';
        } else {
          this.seeall.hidden = true;
        }
        this.applyRefineFilter();
        this.collectItems();
      }

      renderError(qRaw) {
        this.setWorking(false);
        this.empty.hidden = true;
        this.live.hidden = false;
        this.refine.classList.remove("show");
        this.countEl.textContent = "";
        this.live.innerHTML =
          '<div class="ms-zero"><p>Search is taking a moment.</p>' +
          '<div class="sub">Open the full results page to keep going.</div>' +
          '<a class="wa" href="' + this.searchUrl + "?q=" + encodeURIComponent(qRaw) + '">See all results →</a></div>';
        this.seeall.hidden = true;
        this.collectItems();
      }

      zeroHTML(qRaw) {
        // No hardware match: the standard hardware zero-state.
        return (
          '<div class="ms-zero">' +
            "<p>No hardware match for “" + esc(qRaw) + "”</p>" +
            '<div class="sub">' +
            "For hardware, try a brand like <b>Khalil Mamoon</b> or <b>Kaloud</b>, or browse below.</div>" +
            '<div class="ms-pop" style="padding-left:0">' +
              '<a href="' + this.searchUrl + '?q=Charcoal">Charcoal</a>' +
              '<a href="' + this.searchUrl + '?q=Phunnel+bowl">Phunnel bowls</a>' +
              '<a href="' + this.searchUrl + '?q=HMD">HMD</a>' +
              '<a href="' + this.searchUrl + '?q=Hose">Hoses</a>' +
              '<a href="' + this.searchUrl + '?q=Khalil+Mamoon">Khalil Mamoon</a>' +
              '<a href="' + this.searchUrl + '?q=Kaloud">Kaloud</a>' +
            "</div>" +
            '<div style="margin-top:18px"><a class="wa" href="' + WHATSAPP_URL + '" target="_blank" rel="noopener">' +
              '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>' +
              " Ask a specialist</a></div>" +
          "</div>"
        );
      }

      buildRefine(ql) {
        var pills = null;
        for (var k in REFINE) {
          if (ql.indexOf(k) > -1) { pills = REFINE[k]; break; }
        }
        this.refinePills = pills;
        if (!pills) {
          this.refine.classList.remove("show");
          this.refine.innerHTML = "";
          return;
        }
        var self = this;
        var h = '<span class="lbl">Refine</span>';
        h += pills.map(function (t) {
          return '<button type="button" data-t="' + esc(t) + '"' + (self.activeRefine === t ? ' class="on"' : "") + ">" + esc(t) + "</button>";
        }).join("");
        this.refine.innerHTML = h;
        this.refine.classList.add("show");
        this.refine.querySelectorAll("button").forEach(function (btn) {
          btn.addEventListener("click", function () {
            var t = btn.getAttribute("data-t");
            self.activeRefine = self.activeRefine === t ? null : t;
            self.refine.querySelectorAll("button").forEach(function (b) {
              b.classList.toggle("on", b.getAttribute("data-t") === self.activeRefine);
            });
            self.applyRefineFilter();
            self.collectItems();
          });
        });
      }

      applyRefineFilter() {
        var t = this.activeRefine ? this.activeRefine.toLowerCase() : null;
        var rows = this.live.querySelectorAll(".ms-row");
        var visible = 0;
        rows.forEach(function (row) {
          if (!t) { row.hidden = false; visible++; return; }
          var tags = (row.getAttribute("data-tags") || "").toLowerCase();
          var match = tags.indexOf(t) > -1;
          row.hidden = !match;
          if (match) visible++;
        });
        if (t) this.countEl.textContent = visible + (visible === 1 ? " result" : " results");
        else {
          var n = rows.length;
          this.countEl.textContent = n ? n + (n === 1 ? " result" : " results") : "";
        }
        // never leave a blank panel when a refine pill matches nothing in the preview
        var note = this.live.querySelector(".ms-refine-empty");
        if (t && visible === 0) {
          if (!note) {
            note = document.createElement("div");
            note.className = "ms-refine-empty";
            this.live.insertBefore(note, this.live.firstChild);
          }
          note.innerHTML =
            "No “" + esc(this.activeRefine) + "” matches in the top results. " +
            '<a href="' + (this.currentSearchUrl || (this.searchUrl + "?q=" + encodeURIComponent(this.input.value.trim()))) + '">See all results →</a>';
          note.hidden = false;
        } else if (note) {
          note.hidden = true;
        }
      }

      collectItems() {
        // selectable = visible jump rows + product rows currently in the body
        var nodes = this.body.querySelectorAll(".item");
        var self = this;
        this.items = Array.prototype.filter.call(nodes, function (n) { return !n.hidden && n.offsetParent !== null; });
        // A11Y-01: a role="listbox" must contain role="option" children, and the combobox
        // must point aria-activedescendant at the active one. Tag each selectable row as an
        // option with a stable id so the screen reader announces it as we arrow through.
        this.items.forEach(function (n) {
          n.setAttribute("role", "option");
          n.setAttribute("aria-selected", "false");
          if (!n.id) n.id = "ms-opt-" + (++self._uid);
        });
        if (this.sel >= this.items.length) { this.sel = -1; this.input.removeAttribute("aria-activedescendant"); }
      }

      move(d) {
        if (!this.items.length) return;
        if (this.sel > -1 && this.items[this.sel]) {
          this.items[this.sel].classList.remove("sel");
          this.items[this.sel].setAttribute("aria-selected", "false");
        }
        this.sel = (this.sel + d + this.items.length) % this.items.length;
        var cur = this.items[this.sel];
        if (cur) {
          cur.classList.add("sel");
          cur.setAttribute("aria-selected", "true");
          // A11Y-01: announce the active option without moving DOM focus off the input
          this.input.setAttribute("aria-activedescendant", cur.id);
          cur.scrollIntoView({ block: "nearest" });
        }
      }
    }
  );
})();
